import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ShieldCheck, Ship, Plane, TrendingUp } from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1400";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="login-bg relative min-h-screen overflow-hidden text-white">
      {/* faint hero image blend */}
      <img src={HERO} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14] mix-blend-luminosity" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />

      {/* Top bar: XPO left · BMG right */}
      <header className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-12">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-white px-3 py-2">
            <img src="/xpo-logo.png" alt="XPO" className="h-5 w-auto" />
          </div>
          <span className="hidden text-xs font-medium uppercase tracking-[0.2em] text-white/50 sm:inline">
            Business Intelligence
          </span>
        </div>
        <img src="/bmg-logo.png" alt="Brand Machine Group" className="h-7 w-auto sm:h-9" />
      </header>

      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-96px)] max-w-6xl grid-cols-1 items-center gap-12 px-6 pb-16 sm:px-12 lg:grid-cols-2">
        {/* Left — narrative */}
        <div className="rise hidden lg:block">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs uppercase tracking-widest text-white/70">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Freight Forwarding
          </div>
          <h1 className="max-w-lg text-5xl font-semibold leading-[1.05] tracking-tight">
            Technology, visibility &amp;
            <span className="block bg-gradient-to-r from-white to-white/50 bg-clip-text text-transparent">
              data-driven decisions.
            </span>
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/60">
            A single pane of glass across supplier performance, operations, volume and cost —
            powered by CargoWise shipment data for Brand Machine Group.
          </p>

          <div className="mt-12 grid max-w-lg grid-cols-3 gap-4">
            {[
              { icon: Ship, k: "12,480", v: "Shipments tracked" },
              { icon: Plane, k: "24", v: "Sourcing origins" },
              { icon: TrendingUp, k: "18", v: "Insight domains" },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur">
                <s.icon className="mb-3 h-4 w-4 text-brand" />
                <div className="font-mono text-2xl font-semibold">{s.k}</div>
                <div className="mt-1 text-[11px] text-white/50">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — glass form card */}
        <div className="rise delay-1 mx-auto w-full max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-8 shadow-2xl backdrop-blur-2xl sm:p-10">
            <h2 className="text-2xl font-semibold tracking-tight text-white">Sign in</h2>
            <p className="mt-2 text-sm text-white/50">Access your management reporting portal.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  placeholder="you@company.com"
                  className="h-11 border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-brand"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/70">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="••••••••"
                  className="h-11 border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-brand"
                  required
                />
              </div>

              {error ? (
                <p data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="group h-11 w-full gap-2 bg-brand text-white hover:bg-brand/90"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Sign in
                {!loading ? <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /> : null}
              </Button>
            </form>

            <div className="mt-8 flex items-center gap-2 border-t border-white/10 pt-6 text-xs text-white/40">
              <ShieldCheck className="h-3.5 w-3.5" />
              Secured with encrypted session authentication
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
